import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { AddToBasketParams } from 'autogen/swagger/all';
import { ProductDeliveryInfoViewModelQueryKey } from 'autogen/swagger/all/hooks';
import { AddToBasket, ErrorAddToBasket } from 'autogen/translation-keys/trans-website-basket';
import {
    SelectedFrequencyContext,
    SubscriptionContext
} from 'components/pages/PDP/components/MidSection/hooks/context';
import usePowerstep from 'components/powerstep/shared/hooks/usePowerstep';
import { REVIEVE_PRODUCTS_TRACKING_SESSION_KEY } from 'components/product/RevieveVirtualTryOn';
import serverSideTracking from 'components/shared/productItem/shared/helpers';
import { useFindInStoreQuickView } from 'components/storeList/shared/hooks';
import toast from 'components/Toast';
import { useSessionState } from 'hooks/storage';
import usePointOfSaleRequest from 'hooks/user/usePointOfSaleRequest';
import { addToCartTypeEventCategoryMapper, type EventParamDecorated } from 'modules/tracking/GA4';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import { addProductAndSubscriptionToReceipt, addToReceipt } from 'pos/shared/fiftytwo/fiftyTwoApi';
import queryClient from 'setup/react-query/queryClient';
import type { IProductActionButton, IProductActionTrackingContext, ProductActionProps } from '../../shared/types';

export const loadAddToBasketFunc = async () => {
    try {
        const { addToBasket } = await import(
            /* webpackChunkName: "addAndRemoveFromBasket" */ 'modules/addAndRemoveFromBasket'
        );

        return addToBasket;
    } catch (error) {
        throw new Error('failed to load addToBasket');
    }
};

async function addToBasketPos(params: ProductActionProps['BuyNow']['params']) {
    try {
        if (params.subscriptionFrequencyId) {
            await addProductAndSubscriptionToReceipt(
                params.externalProductId.toString(),
                params.subscriptionMinimumSalesQuantity ?? 1,
                params.subscriptionFrequencyId.toString(),
                params.subscriptionDiscountId?.toString()
            );
        } else {
            await addToReceipt(params.externalProductId.toString(), params.minimumSalesQuantity ?? 1);
        }
    } catch (error) {
        throw new Error('Could not add product');
    }
}

export function useAddProduct() {
    const GA4Tracking = useGA4Tracking();
    const [revieveProducts] = useSessionState<number[]>(REVIEVE_PRODUCTS_TRACKING_SESSION_KEY, []);
    const { isPosRequest } = usePointOfSaleRequest();

    function addProductTracking(
        trackingInfo: ProductActionProps['BuyNow']['trackingInfo'],
        additionalGA4Params?: EventParamDecorated[]
    ) {
        const GA4TrackingObject = addToCartTypeEventCategoryMapper(
            trackingInfo.eventCategory,
            trackingInfo.eventLabel?.toString()
        );
        const eventParams = additionalGA4Params
            ? [...additionalGA4Params, ...(GA4TrackingObject.eventParams || [])]
            : GA4TrackingObject.eventParams;

        GA4Tracking({ ...GA4TrackingObject, eventParams });

        if (trackingInfo?.serverSideTrackingModel) {
            serverSideTracking(
                trackingInfo.serverSideTrackingModel.data,
                trackingInfo.serverSideTrackingModel.eventType
            );
        }
    }

    return async function AddProduct(
        { params, trackingInfo }: ProductActionProps['BuyNow'],
        additionalGA4Params?: EventParamDecorated[]
    ) {
        const { productId } = params;
        const paramsWithReviveTracking: AddToBasketParams = {
            ...params,
            usedRevieve: revieveProducts.includes(productId)
        };

        let response;
        try {
            if (isPosRequest) {
                response = await addToBasketPos(params);
            } else {
                const addToBasket = await loadAddToBasketFunc();
                response = await addToBasket(paramsWithReviveTracking);
            }
        } catch (error) {
            throw new Error('Could not add product');
        }

        try {
            addProductTracking(trackingInfo, additionalGA4Params);
        } catch (err) {
            console.error(`Could not track AddProduct: ${err}`);
        }
        return response;
    };
}

export function useAddToBasket(params: IProductActionButton['model'], trackingInfo: IProductActionTrackingContext) {
    const [openPowerstep] = usePowerstep();
    const AddProduct = useAddProduct();
    const { t } = useTranslation();
    const { selectedFrequency } = useContext(SelectedFrequencyContext);
    const { subscriptionActive } = useContext(SubscriptionContext);
    const [, toggle] = useFindInStoreQuickView();

    const isSubscriptionSelected = subscriptionActive && selectedFrequency;

    return async function ClickToAddBasket() {
        try {
            let addProductParams = params;

            if (isSubscriptionSelected) {
                addProductParams = {
                    ...params,
                    subscriptionFrequencyId: selectedFrequency?.id
                };
            } else if (params.storeId) {
                addProductParams = { ...params, action: 'ClickAndCollect' };
            }

            await AddProduct({ params: addProductParams, trackingInfo });

            queryClient.invalidateQueries({ queryKey: [ProductDeliveryInfoViewModelQueryKey] }); // invalidating key so delivery info nudge updates on PDP

            if (params.showPowerstepOnAddToBasket) {
                if (params.storeId) {
                    toggle({ isOpen: false });
                }

                openPowerstep([params.productId], params.storeId);
            } else {
                toast.success<string>(t(AddToBasket), {
                    position: 'top-right'
                });
            }
        } catch (e) {
            toast.error<string>(t(ErrorAddToBasket), {
                position: 'top-right'
            });
            throw new Error(e as string);
        }
    };
}
